import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider } from '@clerk/clerk-react';
import { AuthProvider, NoAuthProvider, useAuth } from './context/AuthContext';
import { isClerkConfigured } from './lib/clerk-config';
import ProtectedRoute from './components/shared/ProtectedRoute';
import AppLoadingScreen from './components/shared/AppLoadingScreen';
import AppShell from './components/shell/AppShell';
import { FLAGS } from './lib/featureFlags';

// ─── Eagerly loaded — must paint fast on first arrival ────────────────────────
// Login + onboarding + the auth-protected MapPage are the most-likely first
// destinations, so they ship in the main bundle. Everything else lazy-loads
// on first navigation, drastically shrinking the initial JS the user
// downloads when they hit /login (where they spend the most time waiting).
import LoginPage         from './app/LoginPage';
import RegisterPage      from './app/RegisterPage';
import MapPage           from './app/MapPage';
import OnboardingPage    from './app/onboarding/OnboardingPage';
import LandingPage       from './app/LandingPage';

// ─── Help drawer / button (top-level imports — must precede `lazy()` calls
// to keep eslint's `import/first` happy and to avoid unnecessary deferral
// of these tiny modules) ─────────────────────────────────────────────────────
import { HelpProvider, HelpDrawer, HelpButton } from './help';
import RouteErrorBoundary from './components/shared/RouteErrorBoundary';
import { ToastProvider } from './lib/toast';
import SkipLink from './components/shared/SkipLink';

// Chunk-hash recovery: after every Pages deploy, hashed chunk filenames
// change — a tab still running the previous bundle 404s when it lazy-loads
// its next route and the user sees a broken page. When that happens, force
// one full reload (which fetches the fresh index.html and matching chunks).
// A sessionStorage guard keeps a genuinely-broken chunk from reload-looping.
function lazyWithReload<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const mod = await factory();
      sessionStorage.removeItem('chunk-reload');
      return mod;
    } catch (err) {
      // Right after a deploy the edge can briefly serve the SPA fallback
      // (index.html) for a chunk URL, and the browser then caches that
      // HTML under the chunk's URL — every later load resurrects it via
      // conditional revalidation. `cache: 'reload'` forces an
      // unconditional fetch AND overwrites the poisoned cache entry, so
      // the retried import below reads the healed cache.
      const chunkUrl =
        (err as { request?: string })?.request ??
        /\bhttps?:\/\/\S+\.js\b/.exec(String((err as Error)?.message ?? ''))?.[0];
      if (chunkUrl) {
        try {
          await fetch(chunkUrl, { cache: 'reload' });
        } catch {
          /* offline — fall through to the retry/reload path */
        }
      }
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const mod = await factory();
        sessionStorage.removeItem('chunk-reload');
        return mod;
      } catch (err2) {
        if (sessionStorage.getItem('chunk-reload')) throw err2;
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        // Keep Suspense pending while the reload happens.
        return new Promise<{ default: T }>(() => {});
      }
    }
  });
}

// Inspector Workspace — personal hub. Lazy because it's a new surface
// most public visitors will never hit.
const WorkspacePage      = lazyWithReload(() => import(/* webpackChunkName: "workspace" */ './app/workspace/WorkspacePage'));

// Fleet Readiness Dashboard — the operator persona's home.
const FleetDashboardPage = lazyWithReload(() => import(/* webpackChunkName: "fleet" */ './app/fleet/FleetDashboardPage'));

// Corrective actions — fleet-level findings board.
const ActionsPage = lazyWithReload(() => import(/* webpackChunkName: "actions" */ './app/actions/ActionsPage'));

// TMSA 3 operator self-assessment (Comply group; fleet-scoped).
const TmsaPage = lazyWithReload(() => import(/* webpackChunkName: "tmsa" */ './app/tmsa/TmsaPage'));

// Public free ship check — the top-of-funnel wedge (no auth).
const ShipCheckPage = lazyWithReload(() => import(/* webpackChunkName: "ship-check" */ './app/ShipCheckPage'));

// ─── Lazy-loaded routes (per-route chunks) ────────────────────────────────────
// `webpackChunkName` comments make the resulting filenames human-readable in
// the build output (`build/static/js/vetting-run.<hash>.chunk.js` etc.) so
// performance work in production traces is easier to attribute.
const VesselSearchPage     = lazyWithReload(() => import(/* webpackChunkName: "vessel-search" */    './app/VesselSearchPage'));
const VesselProfilePage    = lazyWithReload(() => import(/* webpackChunkName: "vessel-profile" */   './app/VesselProfilePage'));
const InspectionListPage   = lazyWithReload(() => import(/* webpackChunkName: "inspection-list" */  './app/InspectionListPage'));
const InspectionDetailPage = lazyWithReload(() => import(/* webpackChunkName: "inspection-detail" */'./app/InspectionDetailPage'));
const FieldInspectionPage  = lazyWithReload(() => import(/* webpackChunkName: "field-inspection" */ './app/FieldInspectionPage'));
const AdminPage            = lazyWithReload(() => import(/* webpackChunkName: "admin" */            './app/AdminPage'));

// Vetting module
const DemoWalkthroughPage      = lazyWithReload(() => import(/* webpackChunkName: "vetting-demo" */    './app/vetting/DemoWalkthroughPage'));
const VettingInspectionsList   = lazyWithReload(() => import(/* webpackChunkName: "vetting-list" */    './app/vetting/InspectionsListPage'));
const VettingNewInspectionPage = lazyWithReload(() => import(/* webpackChunkName: "vetting-new" */     './app/vetting/NewInspectionPage'));
const VettingInspectionDetail  = lazyWithReload(() => import(/* webpackChunkName: "vetting-detail" */  './app/vetting/InspectionDetailPage'));
const VettingInspectionPrep    = lazyWithReload(() => import(/* webpackChunkName: "vetting-prep" */    './app/vetting/InspectionPrepPage'));
const VettingInspectionRun     = lazyWithReload(() => import(/* webpackChunkName: "vetting-run" */     './app/vetting/InspectionRunPage'));
const VettingInspectionReview  = lazyWithReload(() => import(/* webpackChunkName: "vetting-review" */  './app/vetting/InspectionReviewPage'));
const VettingTemplatesPage     = lazyWithReload(() => import(/* webpackChunkName: "vetting-templates" */'./app/vetting/TemplatesPage'));
const VettingSettingsPage      = lazyWithReload(() => import(/* webpackChunkName: "vetting-settings" */ './app/vetting/SettingsPage'));

// Help (knowledge base) — visited rarely, perfect candidate for lazy.
const HelpHomePage = lazyWithReload(() => import(/* webpackChunkName: "help" */ './app/help/HelpHomePage'));

const CLERK_PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY ?? '';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Override Clerk's user-visible copy so users only see "VarunAIq" branding
// in the sign-in/sign-up flows. Keeps users from being alarmed by the
// underlying identity provider's name on first contact.
const VARUNA_LOCALIZATION = {
  signIn: {
    start: {
      title:    'Sign in to VarunAIq',
      subtitle: 'Continue to your maritime compliance workspace.',
      actionText: 'New to VarunAIq?',
      actionLink: 'Create an account',
    },
    password: {
      title:    'Sign in to VarunAIq',
      subtitle: 'Enter your password to continue.',
    },
    forgotPassword: {
      title: 'Reset your VarunAIq password',
    },
    emailCode: {
      title:    'Check your email',
      subtitle: 'We sent a verification code to your inbox.',
    },
    alternativeMethods: {
      title:    'Use another method',
      subtitle: "Choose how you'd like to sign in to VarunAIq.",
    },
  },
  signUp: {
    start: {
      title:    'Create your VarunAIq account',
      subtitle: 'Sign up for the maritime compliance intelligence platform.',
      actionText: 'Already have an account?',
      actionLink: 'Sign in',
    },
    emailCode: {
      title:    'Verify your email for VarunAIq',
      subtitle: 'We sent a verification code to your inbox.',
    },
  },
  userButton: {
    action__signOut: 'Sign out of VarunAIq',
  },
};

export default function App() {
  // Without a usable Clerk key, mounting <ClerkProvider> throws and takes
  // down even public pages (map, vetting demo). Run in no-auth mode instead:
  // signed-out context everywhere, protected routes redirect to /login,
  // which explains the missing configuration.
  if (!isClerkConfigured) {
    console.warn(
      '[auth] REACT_APP_CLERK_PUBLISHABLE_KEY is missing or a placeholder — running in no-auth mode.',
    );
    return <AppTree noAuth />;
  }
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} localization={VARUNA_LOCALIZATION}>
      <AppTree />
    </ClerkProvider>
  );
}

function AppTree({ noAuth = false }: { noAuth?: boolean }) {
  const AuthP = noAuth ? NoAuthProvider : AuthProvider;
  return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthP>
            <ToastProvider>
            <SkipLink />
            <HelpProvider>
              <Suspense fallback={<AppLoadingScreen />}>
                <Routes>
                  {/* ── Public routes (no shell) ───────────────────────────── */}
                  {/* Root: marketing site for anonymous visitors, role home
                      for signed-in users. Lives OUTSIDE AppShell because the
                      shell's ProtectedRoute would bounce visitors to /login
                      before the gate could render the landing page. */}
                  <Route path="/"                   element={<HomeGate />} />
                  <Route path="/welcome"            element={<LandingPage />} />
                  <Route path="/login/*"            element={<LoginPage />} />
                  <Route path="/register/:type"     element={<RegisterPage />} />
                  <Route path="/register"           element={<Navigate to="/register/viewer" replace />} />
                  <Route path="/help"               element={<HelpHomePage />} />
                  <Route path="/help/:topic/:slug"  element={<HelpHomePage />} />
                  <Route path="/vetting/demo"       element={<DemoWalkthroughPage />} />
                  <Route path="/check"              element={<ShipCheckPage />} />

                  {/* ── Onboarding (protected, but skips its own gate) ─────── */}
                  <Route path="/onboarding" element={
                    <ProtectedRoute skipOnboardingCheck>
                      <OnboardingPage />
                    </ProtectedRoute>
                  } />

                  {/* ── App shell: every signed-in surface shares the global
                         sidebar. Role gates stay per-route inside. ─────────── */}
                  <Route element={<AppShell />}>
                    <Route path="/fleet" element={<FleetDashboardPage />} />
                    <Route path="/actions" element={<ActionsPage />} />
                    <Route path="/tmsa" element={<TmsaPage />} />
                    <Route path="/map" element={<MapPage />} />
                    <Route path="/vessels"     element={<VesselSearchPage />} />
                    <Route path="/vessels/:id" element={<VesselProfilePage />} />

                    <Route path="/workspace" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <WorkspacePage />
                      </ProtectedRoute>
                    } />

                    {/* Legacy inspection module (pre-vetting) */}
                    <Route path="/inspections/new" element={<Navigate to="/vetting/inspections/new" replace />} />
                    <Route path="/inspections" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <InspectionListPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/inspections/:id" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <InspectionDetailPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/inspections/:id/field" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <FieldInspectionPage />
                      </ProtectedRoute>
                    } />

                    <Route path="/admin" element={
                      <ProtectedRoute roles={['admin']}>
                        <AdminPage />
                      </ProtectedRoute>
                    } />

                    {/* ── Ship Vetting module (flat under the shell; the old
                           VettingLayout sidebar is superseded by AppShell) ── */}
                    <Route path="/vetting" element={<Navigate to="/vetting/inspections" replace />} />
                    <Route path="/vetting/inspections" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <VettingInspectionsList />
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/inspections/new" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <VettingNewInspectionPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/inspections/:id" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <RouteErrorBoundary fallbackPath="/vetting/inspections">
                          <VettingInspectionDetail />
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/inspections/:id/prep" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <RouteErrorBoundary fallbackPath="/vetting/inspections">
                          <VettingInspectionPrep />
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/inspections/:id/run" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <RouteErrorBoundary fallbackPath="/vetting/inspections">
                          <VettingInspectionRun />
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/inspections/:id/review" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <RouteErrorBoundary fallbackPath="/vetting/inspections">
                          <VettingInspectionReview />
                        </RouteErrorBoundary>
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/templates" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <VettingTemplatesPage />
                      </ProtectedRoute>
                    } />
                    <Route path="/vetting/settings" element={
                      <ProtectedRoute roles={['inspector', 'admin']}>
                        <VettingSettingsPage />
                      </ProtectedRoute>
                    } />
                  </Route>

                  {/* ── Catch-all ──────────────────────────────────────────── */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
              {/* Global help — drawer + floating button — must be inside Router */}
              <HelpDrawer />
              <HelpButton />
            </HelpProvider>
            </ToastProvider>
          </AuthP>
        </BrowserRouter>
      </QueryClientProvider>
  );
}

/**
 * Root gate for varunaiq.com: anonymous visitors see the marketing site at
 * `/` itself (no redirect — the URL people share stays clean); signed-in
 * users go to their role home (inspectors → workspace, operators/admins →
 * fleet). Waits for Clerk to resolve so a signed-in refresh doesn't flash
 * the landing page.
 */
function HomeGate() {
  const { user, role, loading } = useAuth();
  if (loading) return <AppLoadingScreen />;
  if (!user) return <LandingPage />;
  if (role === 'inspector') return <Navigate to="/workspace" replace />;
  return <Navigate to={FLAGS.fleet ? '/fleet' : '/map'} replace />;
}
